// src/components/TranslationPortal.js
import React, { useState, useEffect, useMemo } from 'react';
import { getTranslationJobData, submitTranslation } from '../services/firestoreService';
import { renderMarkdown } from '../services/utils'; // (Optional, für schöne Anzeige)

// Helper-Funktion, um XLIFF-Daten zu parsen
// (Diese Funktion ist vereinfacht. Für eine robuste Lösung wäre eine XML-Parsing-Bibliothek besser)
const parseXliff = (xliffContent, jobData) => {
    const translations = {};
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xliffContent, "application/xml");
    
    // Fehlerprüfung für XML-Parsing
    const parseError = xmlDoc.getElementsByTagName("parsererror")[0];
    if (parseError) {
        console.error("Fehler beim Parsen von XLIFF:", parseError.textContent);
        throw new Error("Ungültige XLIFF-Datei. Konnte XML nicht parsen.");
    }

    const transUnits = xmlDoc.getElementsByTagName("trans-unit");

    for (let i = 0; i < transUnits.length; i++) {
        const unit = transUnits[i];
        const id = unit.getAttribute("id");
        if (!id) continue;

        const targetNode = unit.getElementsByTagName("target")[0];
        // Sicherstellen, dass der Inhalt existiert
        if (targetNode && (targetNode.textContent || targetNode.innerHTML)) {
            translations[id] = targetNode.textContent || targetNode.innerHTML;
        }
    }
    
    // Konvertiere die flache XLIFF-Struktur zurück in das Firestore-Format
    const firestoreContent = {};
    if (translations['title']) {
        firestoreContent['title'] = translations['title'];
    }
    
    const parseAgendaItems = (items) => {
        if (!Array.isArray(items)) return;
        items.forEach(item => {
            const descKey = `item_${item.id}_desc`;
            const notesKey = `item_${item.id}_notes`;
            
            if (translations[descKey]) {
                firestoreContent[descKey] = translations[descKey];
            }
            if (translations[notesKey]) {
                firestoreContent[notesKey] = translations[notesKey];
            }
            
            if (item.subPoints) {
                parseAgendaItems(item.subPoints); // Rekursiv
            }
        });
    };
    parseAgendaItems(jobData.agendaStructure || []);

    if (Object.keys(firestoreContent).length === 0) {
        throw new Error("Die XLIFF-Datei enthielt keine passenden übersetzten <target>-Elemente.");
    }

    return firestoreContent;
};

// Helper-Funktion, um Firestore-Daten in XLIFF zu generieren
const generateXliff = (jobData) => {
    const { vorgangsNummer, originalLanguage, targetLanguage, agendaStructure, originalContent } = jobData;
    
    let xliff = `<?xml version="1.0" encoding="UTF-8"?>
<xliff xmlns="urn:oasis:names:tc:xliff:document:1.2" version="1.2">
  <file original="meetform-protocol" source-language="${originalLanguage}" target-language="${targetLanguage}" datatype="plaintext">
    <header>
      <tool tool-id="meetform" tool-name="Meetform Translation" tool-version="1.0"/>
      <note>Vorgangsnummer: ${vorgangsNummer}</note>
    </header>
    <body>
`;

    // 1. Titel
    if (originalContent.title) {
        const title = (originalContent.title || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        xliff += `
      <trans-unit id="title">
        <source>${title}</source>
        <target state="new"></target>
      </trans-unit>`;
    }

    // 2. Agenda-Punkte (rekursiv)
    const addAgendaUnits = (items) => {
        if (!Array.isArray(items)) return;
        items.forEach(item => {
            const descKey = `item_${item.id}_desc`;
            const notesKey = `item_${item.id}_notes`;
            
            if (originalContent[descKey]) {
                const desc = (originalContent[descKey] || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                xliff += `
      <trans-unit id="${descKey}">
        <source>${desc}</source>
        <target state="new"></target>
      </trans-unit>`;
            }
            if (originalContent[notesKey]) {
                const notes = (originalContent[notesKey] || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                xliff += `
      <trans-unit id="${notesKey}">
        <source>${notes}</source>
        <target state="new"></target>
      </trans-unit>`;
            }
            
            if (item.subPoints) {
                addAgendaUnits(item.subPoints);
            }
        });
    };
    
    addAgendaUnits(agendaStructure || []);

    xliff += `
    </body>
  </file>
</xliff>`;
    
    return xliff;
};


// Die Haupt-Portal-Komponente
const TranslationPortal = ({ token, setNotification, onHomeClick }) => {
    const [jobData, setJobData] = useState(null);
    const [error, setError] = useState(null);
    // === NEUER STATE FÜR ERFOLGSMELDUNGEN ===
    const [successMessage, setSuccessMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('manual'); // 'manual' or 'xliff'
    
    // Zustand für die manuelle Eingabe
    const [manualTranslations, setManualTranslations] = useState({});

    // Zustand für XLIFF-Upload
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 1. Lade die Job-Daten beim Start
    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);
                const data = await getTranslationJobData(token);
                setJobData(data);
                
                // Initialisiere manuelle Übersetzungs-Felder mit existierenden (oder leeren) Daten
                const initialTranslations = {};
                if (data.originalContent.title) {
                    initialTranslations['title'] = data.targetContent.title || '';
                }
                
                const initAgendaTranslations = (items) => {
                    if (!Array.isArray(items)) return;
                    items.forEach(item => {
                        initialTranslations[`item_${item.id}_desc`] = data.targetContent[`item_${item.id}_desc`] || '';
                        initialTranslations[`item_${item.id}_notes`] = data.targetContent[`item_${item.id}_notes`] || '';
                        if (item.subPoints) {
                            initAgendaTranslations(item.subPoints); // Rekursiv
                        }
                    });
                };
                initAgendaTranslations(data.agendaStructure || []);
                
                setManualTranslations(initialTranslations);
                setError(null);
                setSuccessMessage(null);
            } catch (err) {
                console.error(err);
                setError(err.message || "Fehler beim Laden der Daten.");
                setSuccessMessage(null);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, [token]);

    // 2. Handler für manuelle Eingabe
    const handleManualChange = (key, value) => {
        setManualTranslations(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // 3. Handler für XLIFF-Download
    const handleDownloadXliff = () => {
        try {
            const xliffData = generateXliff(jobData);
            const blob = new Blob([xliffData], { type: 'application/xliff+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${jobData.vorgangsNummer}.xliff`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error("Fehler beim Generieren der XLIFF:", e);
            setError("Fehler beim Generieren der XLIFF-Datei.");
        }
    };

    // 4. Handler für XLIFF-Upload
    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setUploadedFile(file);
            setError(null); // Fehler zurücksetzen, wenn neue Datei gewählt wird
        }
    };
    
    // 5. Handler für das Einreichen (beide Methoden)
    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);
        setSuccessMessage(null);
        let contentToSubmit = {};

        try {
            if (activeTab === 'manual') {
                contentToSubmit = manualTranslations;
            } else {
                // XLIFF-Modus
                if (!uploadedFile) {
                    throw new Error("Bitte wählen Sie die übersetzte XLIFF-Datei aus.");
                }
                const fileContent = await uploadedFile.text();
                contentToSubmit = parseXliff(fileContent, jobData);
            }
            
            // Sende die Daten an die Cloud Function
            await submitTranslation(token, contentToSubmit);
            
            // === GEÄNDERT: Erfolg über setSuccessMessage setzen ===
            setSuccessMessage("Übersetzung erfolgreich eingereicht! Dieses Fenster kann jetzt geschlossen werden.");
            setNotification({ message: 'Übersetzung erfolgreich eingereicht!', type: 'success' });
            // Deaktiviere das Formular nach Erfolg
            setJobData(null); 
            
        } catch (err) {
            console.error("Error submitting translation:", err);
            // === GEÄNDERT: Fehler über setError setzen ===
            setError(err.message || "Ein Fehler ist aufgetreten.");
            setNotification({ message: `Fehler: ${err.message}`, type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- RENDER-LOGIK ---

    if (isLoading) {
        return <div className="text-center p-12 text-gray-700 dark:text-gray-300">Lade Übersetzungsportal...</div>;
    }

    // === GEÄNDERT: Eigene Anzeige für Erfolg oder Fehler ===
    if (error || successMessage || !jobData) {
        return (
            <div className="p-8 bg-white dark:bg-gray-800 rounded-lg shadow-xl text-center">
                {error && (
                    <>
                        <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">Fehler</h2>
                        <p className="text-lg text-gray-700 dark:text-gray-300">{error}</p>
                    </>
                )}
                {successMessage && (
                     <>
                        <h2 className="text-2xl font-bold text-green-600 dark:text-green-400 mb-4">Erfolgreich</h2>
                        <p className="text-lg text-gray-700 dark:text-gray-300">{successMessage}</p>
                    </>
                )}
                {!error && !successMessage && (
                    <p className="text-lg text-gray-700 dark:text-gray-300">Auftrag nicht gefunden oder bereits abgeschlossen.</p>
                )}
                <button onClick={onHomeClick} className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg">Zur Startseite</button>
            </div>
        );
    }
    
    const { vorgangsNummer, originalLanguage, targetLanguage, agendaStructure, originalContent } = jobData;

    // Rekursive Render-Funktion für manuelle Eingabefelder
    const renderManualFields = (items, level = 0) => {
        if (!Array.isArray(items)) return null;
        
        const paddingLeft = `${level * 1.5}rem`;
        
        return items.map(item => {
            const descKey = `item_${item.id}_desc`;
            const notesKey = `item_${item.id}_notes`;
            
            // Nur rendern, wenn es ein Original gibt
            if (!originalContent[descKey]) return null;

            return (
                <div key={item.id} className="p-4 border dark:border-gray-700 rounded-lg space-y-3" style={{ marginLeft: paddingLeft }}>
                    {/* Beschreibung */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-500 dark:text-gray-400">
                            Agendapunkt (Original: {originalLanguage.toUpperCase()})
                        </label> 
                        <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-md text-gray-800 dark:text-gray-200 min-h-[40px]" dangerouslySetInnerHTML={{ __html: renderMarkdown(originalContent[descKey] || '') }} />
                        <label className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mt-2">
                            Agendapunkt (Übersetzung: {targetLanguage.toUpperCase()})
                        </label>
                        <textarea
                            value={manualTranslations[descKey] || ''}
                            onChange={(e) => handleManualChange(descKey, e.target.value)}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-900 min-h-[60px] text-gray-900 dark:text-white"
                            rows={2}
                        />
                    </div>
                    
                    {/* Notizen (falls vorhanden) */}
                    {originalContent[notesKey] && (
                        <div className="mt-4">
                            <label className="block text-sm font-semibold text-gray-500 dark:text-gray-400">Notizen (Original: {originalLanguage.toUpperCase()})</label>
                            <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-md text-gray-800 dark:text-gray-200 min-h-[40px]" dangerouslySetInnerHTML={{ __html: renderMarkdown(originalContent[notesKey] || '') }} />
                            <label className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mt-2">Notizen (Übersetzung: {targetLanguage.toUpperCase()})</label>
                            <textarea
                                value={manualTranslations[notesKey] || ''}
                                onChange={(e) => handleManualChange(notesKey, e.target.value)}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-900 min-h-[60px] text-gray-900 dark:text-white"
                                rows={2}
                            />
                        </div>
                    )}
                    
                    {/* Unterpunkte (Rekursion) */}
                    {item.subPoints && item.subPoints.length > 0 && (
                        <div className="mt-4">
                            {renderManualFields(item.subPoints, level + 1)}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <div className="p-4 md:p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-4xl mx-auto my-8">
            <header className="border-b dark:border-gray-700 pb-4 mb-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Meetform Übersetzer-Portal</p>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                    Auftrag: <span className="text-blue-600 dark:text-blue-400">{vorgangsNummer}</span>
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300">
                    Sprache: <span className="font-semibold">{originalLanguage.toUpperCase()}</span> &rarr; <span className="font-semibold">{targetLanguage.toUpperCase()}</span>
                </p>
            </header>

            {/* Tab-Umschalter (Manuell vs. XLIFF) */}
            <div className="flex mb-6 border-b dark:border-gray-600">
                <button 
                    onClick={() => setActiveTab('manual')}
                    className={`px-6 py-3 font-semibold ${activeTab === 'manual' ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                >
                    Manuell im Browser
                </button>
                <button 
                    onClick={() => setActiveTab('xliff')}
                    className={`px-6 py-3 font-semibold ${activeTab === 'xliff' ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                >
                    Profi-Workflow (XLIFF)
                </button>
            </div>

            {/* === Tab-Inhalt: Manuell === */}
            {activeTab === 'manual' && (
                <div className="space-y-6">
                    {/* Titel */}
                    <div className="p-4 border dark:border-gray-700 rounded-lg">
                        <label className="block text-sm font-semibold text-gray-500 dark:text-gray-400">Titel (Original: {originalLanguage.toUpperCase()})</label>
                        <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-md text-gray-800 dark:text-gray-200 min-h-[40px]" dangerouslySetInnerHTML={{ __html: renderMarkdown(originalContent.title || '') }} />
                        <label className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mt-2">Titel (Übersetzung: {targetLanguage.toUpperCase()})</label>
                        <input
                            type="text"
                            value={manualTranslations['title'] || ''}
                            onChange={(e) => handleManualChange('title', e.target.value)}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-900 text-gray-900 dark:text-white"
                        />
                    </div>
                    
                    {/* Agenda-Punkte (Rekursiv) */}
                    {renderManualFields(agendaStructure)}
                </div>
            )}
            
            {/* === Tab-Inhalt: XLIFF === */}
            {activeTab === 'xliff' && (
                <div className="space-y-8">
                    {/* Schritt 1: Download */}
                    <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Schritt 1: Originaldatei herunterladen</h4>
                        <p className="text-gray-600 dark:text-gray-300 mb-3">Laden Sie die XLIFF-Datei herunter und bearbeiten Sie sie in Ihrer Übersetzungssoftware.</p>
                        <button
                            onClick={handleDownloadXliff}
                            className="px-5 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition"
                        >
                            XLIFF herunterladen
                        </button>
                    </div>
                    
                    {/* Schritt 2: Upload */}
                    <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Schritt 2: Übersetzte Datei hochladen</h4>
                        <p className="text-gray-600 dark:text-gray-300 mb-3">Laden Sie die übersetzte XLIFF-Datei hier hoch, um die Übersetzung einzureichen.</p>
                        <input
                            type="file"
                            onChange={handleFileUpload}
                            accept=".xliff, .xml"
                            className="w-full text-sm text-gray-500 dark:text-gray-300
                                file:mr-4 file:py-2 file:px-4
                                file:rounded-full file:border-0
                                file:text-sm file:font-semibold
                                file:bg-blue-50 dark:file:bg-gray-700
                                file:text-blue-700 dark:file:text-blue-300
                                hover:file:bg-blue-100 dark:hover:file:bg-gray-600"
                        />
                        {uploadedFile && (
                            <p className="text-green-600 dark:text-green-400 text-sm mt-2">Datei ausgewählt: {uploadedFile.name}</p>
                        )}
                    </div>
                </div>
            )}
            
            {/* === Einreichen-Button (für beide Tabs) === */}
            <div className="mt-8 pt-6 border-t dark:border-gray-700">
                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 bg-green-600 text-white rounded-lg text-lg font-bold shadow-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                    {isSubmitting ? "Wird eingereicht..." : `Übersetzung (${activeTab === 'manual' ? 'Manuell' : 'XLIFF'}) einreichen`}
                </button>
            </div>
            
        </div>
    );
};

export default TranslationPortal;